import { inject, Injectable } from '@angular/core';
import { Auth, authState, browserLocalPersistence, sendPasswordResetEmail, setPersistence, user, User } from '@angular/fire/auth';
import { deleteField, doc, docData, Firestore, setDoc, updateDoc } from '@angular/fire/firestore';
import { signInWithEmailAndPassword } from '@firebase/auth';
import { BehaviorSubject, map, Observable, of, switchMap, tap } from 'rxjs';
import { Router } from '@angular/router';
import { PathConstants } from '../../db.paths';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private userSubject: BehaviorSubject<any> = new BehaviorSubject<any>(undefined);
  private afAuth = inject(Auth);
  private authState$ = authState(this.afAuth);
  private firestore = inject(Firestore);
  private router = inject(Router);
  referToAttorneys: boolean = false;

  constructor() {
    this.authState$
      .pipe(
        switchMap((user: User | null) => {
          if (!user?.uid) {
            return of(null);
          }

          const path = PathConstants.getUserDetailsPath(user.uid);
          const docRef = doc(this.firestore, path);

          return docData<any>(docRef, { idField: 'id' }).pipe(
            tap((userData) => {
              if (userData) {
                const currentUrl = this.router.url;
                if (currentUrl === '/login' || currentUrl === '/') {
                  this.router.navigate(['/dashboard']);
                }
              } else {
                // This case might happen on first login if the user doc isn't created yet.
                // The login component is responsible for the initial redirect.
                // If a user exists in auth but not in firestore, they should probably be logged out.
                this.logout();
              }
            }),
            map((userData) =>
              !userData ? null : { ...userData, metadata: user.metadata }
            )
          );
        })
      )
      .subscribe((user) => {
        this.userSubject.next(user);
      });
  }

  updateUserIfo(user: any, uid: any) {
    console.log(user, 'updates user info');
    const path = PathConstants.getUserDetailsPath(uid);
    const userDocRef = doc(this.firestore, path);
    return updateDoc(userDocRef, user);
  }

  updateUserSettings(uid: string, settings: any) {
    const path = PathConstants.getUserDetailsPath(uid);
    const userDocRef = doc(this.firestore, path);

    return updateDoc(userDocRef, {
      [`settings.appNotification`]: settings.appNotification,
      [`settings.emailNotification`]: settings.emailNotification,
      [`settings.smsNotification`]: settings.smsNotification,
    });
  }

  addUserSign(signature: any, uid: any) {
    const path = PathConstants.getUserDetailsPath(uid);
    const userDocRef = doc(this.firestore, path);
    const dataToUpdate = { esignData: signature };

    return setDoc(userDocRef, dataToUpdate, { merge: true });
  }

  deleteUserSign(uid: string) {
    const path = PathConstants.getUserDetailsPath(uid);
    const userDocRef = doc(this.firestore, path);

    const dataToUpdate = {
      esignData: deleteField(),
    };

    return updateDoc(userDocRef, dataToUpdate);
  }

  getLoggedInUser(): Observable<any> {
    return this.userSubject.asObservable();
  }

  isValidLogin(): Observable<boolean> {
    return user(this.afAuth).pipe(map((user) => !!user));
  }

  async login(email: string, password: string) {
    await setPersistence(this.afAuth, browserLocalPersistence);
    return signInWithEmailAndPassword(this.afAuth, email, password);
  }

  logout() {
    return this.afAuth.signOut().then(() => {
      this.userSubject.next(null);
      this.router.navigate(['/login']);
    });
  }

  resetPassword(email: string) {
    return sendPasswordResetEmail(this.afAuth, email);
  }
}